import type { IntlShape } from 'react-intl';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import {
	assign,
	createMachine,
	enqueueActions,
	fromCallback,
	StateMachine,
	type AnyActorRef,
	type EventObject,
	type MetaObject,
	type NonReducibleUnknown,
	type ParameterizedObject,
	type ProvidedActor,
	type StateValue,
} from 'xstate';

import type { DocNode } from '@atlaskit/adf-schema';
import type { JSONDocNode } from '@atlaskit/editor-json-transformer';
import type { GetMentionNameDetails } from '@atlaskit/editor-mention-types/types';
import type { NodeType } from '@atlaskit/editor-prosemirror/model';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { fg } from '@atlaskit/platform-feature-flags';
import type { IDMap } from '@atlassian/ai-model-io/convert-prosemirror-to-markdown/serializer';
import type { ContentStatistics } from '@atlassian/ai-model-io/utils/prosemirror-content-statistics';

import type {
	ConvoAIResponseRovoAction,
	EditorPluginAIProvider,
	EndExperience,
	PluginFeedbackMetadata,
	RovoActions,
} from '../../../types/types';
import { CONFIG_ITEM_KEYS } from '../../prebuilt/config-items/config-item-keys';
import type {
	AnalyticsContext,
	EditorPluginAIConfigItem,
	SelectionType,
} from '../../prebuilt/config-items/config-items';
import { buildChannelId } from '../agents/build-channel-id';
import type { AiLifeCycleDynamicAttributesGetter } from '../analytics/analyticsFlowTypes';
import { FAILURE_REASON } from '../errors';
import { hasTextualContent } from '../selection';
import type { StreamingHandler } from '../streaming';

import { GenericActorStore } from './generic-actor-store';

export const aiExperienceActorStore: GenericActorStore<
	StateMachine<
		AIExperienceMachineContext,
		| {
				context: {
					configItem: EditorPluginAIConfigItem;
					parentPresetPromptLabel?: string;
					userADFInput?: JSONDocNode;
					userInput: string;
				};
				type: 'kickoff with command palette input';
		  }
		| {
				adfResponse?: DocNode;
				loadingStatus?: string;
				markdown: string;
				type: 'stream';
		  }
		| {
				adfResponse?: DocNode;
				cacheKey: string;
				inputOutputDiffRatio?: string;
				markdown: string;
				modelInput: ModelInput;
				rovoActions?: ConvoAIResponseRovoAction[];
				type: 'complete';
		  }
		| {
				cacheKey?: string;
				errorInfo: {
					apiName?: string;
					errorContent?: string;
					errorKey?: string;
					failureReason: FAILURE_REASON;
					statusCode?: number;
				};
				modelInput?: ModelInput;
				retryAfter?: number;
				type: 'error';
		  }
		| {
				type: 'retry';
		  }
		| {
				latestPromptResponse: string;
				type: 'interrogate';
				userADFInput?: JSONDocNode;
				userInput: string;
		  }
		| {
				configItem: EditorPluginAIConfigItem;
				latestPromptResponse: string;
				type: 'refine';
		  }
		| {
				type: 'kick off';
		  }
		| {
				type: 'same cache key';
		  }
		| {
				positions: Positions;
				type: 'update positions';
		  }
		| {
				input: string;
				type: 'update user input';
		  }
		| {
				adf?: JSONDocNode;
				type: 'update user input adf';
		  }
		| {
				adf: JSONDocNode | undefined;
				input: string | undefined;
				type: 'retry prompt';
		  }
		| {
				presetTitle?: string;
				type: 'edit prompt';
				userADFInput?: JSONDocNode;
				userInput?: string;
		  }
		| {
				context: {
					configItem: EditorPluginAIConfigItem;
					userADFInput?: JSONDocNode;
					userInput: string;
				};
				type: 'update context';
		  }
		| {
				context: {
					idMap: IDMap;
				};
				type: 'update idMap';
		  }
		| {
				context: {
					traceIds: string[];
				};
				type: 'update traceIds';
		  }
		| {
				context: {
					selectionMetaData: {
						sliceOpenEnd?: number;
						sliceOpenStart?: number;
					};
				};
				type: 'update selectionMetaData';
		  }
		| {
				context: {
					adfResponseNodeWrappers: string[];
				};
				type: 'update adfResponseNodeWrappers';
		  }
		| {
				type: 'start refinement';
		  }
		| {
				type: 'cancel refinement';
		  }
		| {
				context?: { onceDiscard?: () => unknown };
				type: 'enable discard';
		  }
		| {
				type: 'disable discard';
		  }
		| {
				type: 'escape';
		  }
		| {
				contextStatistics: ContextStatistics;
				type: 'storeContextStatistics';
		  }
		| {
				adfResponse?: DocNode;
				cacheKey: string;
				inputOutputDiffRatio?: string;
				modelInput: ModelInput;
				type: 'response too similar';
		  }
		| {
				adfResponse: DocNode;
				type: 'retry on adf stream errors';
		  }
		| {
				sentiment: PluginFeedbackMetadata['sentiment'];
				type: 'feedback submitted';
		  }
		| {
				type: 'toggle use page knowledge';
		  }
		| {
				baseGenerate: EditorPluginAIConfigItem;
				configItem: EditorPluginAIConfigItem;
				type: 'update baseGenerate and configItem';
		  }
		| {
				type: 'reset';
		  }
		| {
				context: AIExperienceMachineContext;
				type: 'initialise context';
		  }
		| {
				type: 'startStreamingForCreate';
		  },
		Record<string, AnyActorRef>,
		ProvidedActor,
		ParameterizedObject,
		ParameterizedObject,
		string,
		StateValue,
		string,
		unknown,
		NonReducibleUnknown,
		EventObject,
		MetaObject,
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		any
	>
> = new GenericActorStore<ReturnType<typeof createAIExperienceMachine>>();

/**
 * This type models when there is a "generate prompt" being used.
 *
 * interrogates the prompt.
 */
type LatestGeneratePrompt = {
	presetTitle?: never;
	userADFInput?: JSONDocNode;
	userInput: string;
};
/**
 * This type models when there is a non-generative preset prompt being used.
 * @example
 * Example of Improve writing:
 * ```
 * {
 *   // config item used for latest prompt
 *   presetTitle: EditorPluginAIConfigItem,
 * }
 * ```
 * @example
 * Example of Brainstorm:
 * ```
 * {
 *   // config item used for latest prompt
 *   presetTitle: EditorPluginAIConfigItem,
 *   userInput: 'Facts about cats',
 * }
 * ```
 */
type LatestPresetPrompt = {
	presetTitle: EditorPluginAIConfigItem;
	userADFInput?: JSONDocNode;
	userInput?: string;
};

export type LatestPrompt = LatestGeneratePrompt | LatestPresetPrompt;

export type PromptTrigger = 'interrogate' | 'refinementButton' | 'generate' | 'custom';

/**
 * For empty selections, the start and end will be the same
 */
export type Positions = [start: number, end: number];

export type ExperienceMachineOptions = {
	context: AIExperienceMachineContext;
};

export type HistoryEvent = {
	cacheKey: string;
	configItem: EditorPluginAIConfigItem;
	inputContentStatistics?: ContentStatistics;
	inputOutputDiffRatio?: string;
	latestPrompt: LatestPrompt;
	markdown: string;
	modelInput: ModelInput;
	responseTooSimilar?: boolean;
	userADFInput?: JSONDocNode;
	userInput: string;
};

export type ContextStatistics = {
	inputDocument: ContentStatistics;
};

export type AIExperienceMachineContext = {
	/**
	 * We are experimenting with communicating with LLM using ADF for some prompts.
	 * So for any prompt when `adfPrompt` is true, we will send the ADF and
	 * 	response will also be in ADF.
	 * In that case, response will be stored in `adfResponse` and markdown will be empty string.
	 */
	adfResponse?: DocNode;
	/**
	 * During ADF streaming, there may be multiple node wrappers that need added to the Slice.
	 * We need to remove these node wrappers from the Slice before inserting it to the doc.
	 */
	adfResponseNodeWrappers?: string[];
	/**
	 * Getter function to dynamically inject attributes to the AI lifecyle events such as `aiInteraction initiated`
	 */
	aiLifeCycleDynamicAttributesGetter?: AiLifeCycleDynamicAttributesGetter;
	aiProvider: EditorPluginAIProvider;
	/** The analytics context for the current user AI Experience journey. */
	analyticsContext: AnalyticsContext;
	apiErrorInfo: {
		apiName?: string;
		errorContent?: string;
		errorKey?: string;
		failureReason?: FAILURE_REASON;
		statusCode?: number;
	};
	// This is added so;
	// - the kick state guard can pick if the config item is the base generate
	// Note -- currently only the empty base generate is used (IC4 adds a
	// base generate for range selections)
	baseGenerate?: EditorPluginAIConfigItem;

	/**
	 * The channelId for a stateful assistance-service chat session.
	 */
	channelId?: string;
	configItem: EditorPluginAIConfigItem;
	/**
	 * This stores stats such as nodeCounts, markCounts etc of context contents sent to the BE
	 * such as the context document (and in future, also the prompt user input context)
	 */
	contextStatistics?: ContextStatistics;
	editCount: number;

	editorView: EditorView;

	endExperience: EndExperience;
	feedbackSubmitted?: PluginFeedbackMetadata['sentiment'];
	/**
	 * Helper function to get the mention name details for a given mention id.
	 * We can no longer get the resolved mention name from the node attributes
	 * so we need to fetch it from the mention service.
	 */
	getMentionNameDetails?: GetMentionNameDetails;
	/**
	 * This is set when converting prosemirror to markdown, and consumed when converting markdown received from the API back to prosemirror.
	 *
	 * It won't be used by all prompts, and can be undefined.
	 *
	 * Currently this is opinionated about only one thing being sent to the llm having an idMap (ie. if a full document is sent -- it will be only be converted to markdown)
	 */
	idMap?: IDMap;
	intl: IntlShape;
	isEmpty: boolean;
	/**
	 *
	 */
	latestPrompt?: LatestPrompt;
	/**
	 * Iniitial sketching of handling `latestPromptResponse` being passed/handled
	 * at various areas, leave in context for now
	 */
	latestPromptResponse?: string;
	latestPromptRetried: boolean;
	/**
	 * This stores the loading status that will be used to update the "Generating" message on the
	 * Loading Screen.
	 */
	loadingStatus?: string;
	markdown?: string;
	/**
	 * function to call when Discard is confirmed
	 * which will be reset by "disable discard" before being called
	 */
	onceDiscard?: () => unknown;
	/**
	 * Label for the parent if the user selects a submenu via a parent (`Change tone` -> `Neutral`)
	 * If the user selects a submenu directly, this will be empty (search and select `Change tone to Neutral`)
	 */
	parentPresetPromptLabel?: string;
	positions: [number, number];

	previousAgentId?: string;

	/**
	 * Used to track what/where the prompt came from. promptTrigger
	 * is mapped in analytic events to promptType (though promptType
	 * is sometimes manually evaluated in other events e.g. prompt userFlow).
	 */
	promptTrigger?: PromptTrigger;

	/**
	 * This is used to track the number of times the user has refined
	 * (clicked by Refine button in the Preview screen) the prompt.
	 */
	refinementCount: number;

	/**
	 * This is used to support returning to the previous response when the user resubmits the same input to prevent additional API calls.
	 * Interrogate and retry prompt make fresh API requests and do not read from this.
	 */
	responseHistory: {
		entries: HistoryEvent[];
		positionFromEnd: number;
		totalSize: number;
		traversals: number;
	};

	retryPromptCount: number;

	rovoActions?: RovoActions;

	/**
	 * This is used to store the selection metadata for the current selection.
	 */
	selectionMetaData?: {
		sliceOpenEnd?: number;
		sliceOpenStart?: number;
	};

	selectionType: SelectionType;
	/**
	 * A streaming function that handles processing the network request data
	 * and passing data back to this state machine.
	 */
	streamingHandler?: StreamingHandler;
	/**
	 * traceId received in response header from AI api call
	 */
	traceIds?: string[];

	triggeredFor?: NodeType;

	usePageKnowledge: boolean;

	/**
	 * When editor is used as prompt input, we will store ADF along side userInput.
	 */
	userADFInput?: JSONDocNode;

	/**
	 * This is input that the user types into either
	 * - the base generate prompt
	 * - or a "custom prompt" (ie. brainstorm)
	 */
	/**
	 * The userInput is intended to contain the latest contents of the user input field on
	 * the prompt input screens, and the refinement screen.
	 */
	userInput?: string;
};

type OptionalKeys<T> = {
	[K in keyof T]: undefined extends T[K] ? K : never;
}[keyof T];
type PickOptional<T> = Required<Pick<T, OptionalKeys<T>>>;
type AddUndefined<T> = {
	[K in keyof T]: T[K] | undefined;
};

// This picks all the optional properties and ensures they are reset to undefined
type InitialContextType = AddUndefined<PickOptional<AIExperienceMachineContext>>;
// These should get reset when we initialise context, but resetting anyways
type ResetRequiredProps = Omit<
	AIExperienceMachineContext,
	'aiProvider' | 'intl' | 'endExperience' | 'editorView' | 'configItem'
>;

// Resets to clean context - all optional properties are ensured to reset via typescript
// Remaining required properties will be reset via `initialise context`.
const initialContext: InitialContextType & ResetRequiredProps = {
	analyticsContext: { aiSessionId: '', aiGroupId: '' },
	positions: [0, 0],
	isEmpty: true,
	selectionType: 'empty',
	markdown: '',
	adfResponse: undefined,
	latestPromptResponse: undefined,
	editCount: 0,
	refinementCount: 0,
	retryPromptCount: 0,
	latestPromptRetried: false,
	userInput: '',
	apiErrorInfo: {},
	responseHistory: { positionFromEnd: 0, entries: [], totalSize: 0, traversals: 0 },
	channelId: undefined,
	usePageKnowledge: true,
	baseGenerate: undefined,
	userADFInput: undefined,
	parentPresetPromptLabel: undefined,
	latestPrompt: undefined,
	promptTrigger: undefined,
	idMap: undefined,
	contextStatistics: undefined,
	loadingStatus: undefined,
	rovoActions: undefined,
	previousAgentId: undefined,
	getMentionNameDetails: undefined,
	onceDiscard: undefined,
	triggeredFor: undefined,
	aiLifeCycleDynamicAttributesGetter: undefined,
	feedbackSubmitted: undefined,
	streamingHandler: undefined,
	traceIds: undefined,
	selectionMetaData: undefined,
	adfResponseNodeWrappers: undefined,
};

/**
 * This is a state machine for the AI Experience. Consider this the source
 * of truth for the AI modal the moment it appears to the moment it closes.
 *
 * The state is initialised in ExperienceApplicationStateWrapper via
 * useAIExperienceService().
 *
 * The state machine can be updated from React components via send() or
 * streaming() as the generated response is streamed back from endpoint.
 *
 * Use the XState extension to access a visual editor for the state machine.
 * When making changes to the state machine, please update the visualisation
 * and add a screenshot of the new layout in your PR.
 *
 * @see https://hello.atlassian.net/wiki/spaces/EDAI/pages/3222977194/HOWTO+-+Using+XState+state+machine+in+Editor+AI#How-do-I-update-the-%40xstate-layout-diagram-in-code%3F
 */
export function createAIExperienceMachine(options: ExperienceMachineOptions): StateMachine<
	AIExperienceMachineContext,
	| {
			context: {
				configItem: EditorPluginAIConfigItem;
				parentPresetPromptLabel?: string;
				userADFInput?: JSONDocNode;
				userInput: string;
			};
			type: 'kickoff with command palette input';
	  }
	| {
			adfResponse?: DocNode;
			loadingStatus?: string;
			markdown: string;
			type: 'stream';
	  }
	| {
			adfResponse?: DocNode;
			cacheKey: string;
			inputOutputDiffRatio?: string;
			markdown: string;
			modelInput: ModelInput;
			rovoActions?: ConvoAIResponseRovoAction[];
			type: 'complete';
	  }
	| {
			cacheKey?: string;
			errorInfo: {
				apiName?: string;
				errorContent?: string;
				errorKey?: string;
				failureReason: FAILURE_REASON;
				statusCode?: number;
			};
			modelInput?: ModelInput;
			retryAfter?: number;
			type: 'error';
	  }
	| {
			type: 'retry';
	  }
	| {
			latestPromptResponse: string;
			type: 'interrogate';
			userADFInput?: JSONDocNode;
			userInput: string;
	  }
	| {
			configItem: EditorPluginAIConfigItem;
			latestPromptResponse: string;
			type: 'refine';
	  }
	| {
			type: 'kick off';
	  }
	| {
			type: 'same cache key';
	  }
	| {
			positions: Positions;
			type: 'update positions';
	  }
	| {
			input: string;
			type: 'update user input';
	  }
	| {
			adf?: JSONDocNode;
			type: 'update user input adf';
	  }
	| {
			adf: JSONDocNode | undefined;
			input: string | undefined;
			type: 'retry prompt';
	  }
	| {
			presetTitle?: string;
			type: 'edit prompt';
			userADFInput?: JSONDocNode;
			userInput?: string;
	  }
	| {
			context: {
				configItem: EditorPluginAIConfigItem;
				userADFInput?: JSONDocNode;
				userInput: string;
			};
			type: 'update context';
	  }
	| {
			context: {
				idMap: IDMap;
			};
			type: 'update idMap';
	  }
	| {
			context: {
				traceIds: string[];
			};
			type: 'update traceIds';
	  }
	| {
			context: {
				selectionMetaData: {
					sliceOpenEnd?: number;
					sliceOpenStart?: number;
				};
			};
			type: 'update selectionMetaData';
	  }
	| {
			context: {
				adfResponseNodeWrappers: string[];
			};
			type: 'update adfResponseNodeWrappers';
	  }
	| {
			type: 'start refinement';
	  }
	| {
			type: 'cancel refinement';
	  }
	| {
			context?: { onceDiscard?: () => unknown };
			type: 'enable discard';
	  }
	| {
			type: 'disable discard';
	  }
	| {
			type: 'escape';
	  }
	| {
			contextStatistics: ContextStatistics;
			type: 'storeContextStatistics';
	  }
	| {
			adfResponse?: DocNode;
			cacheKey: string;
			inputOutputDiffRatio?: string;
			modelInput: ModelInput;
			type: 'response too similar';
	  }
	| {
			adfResponse: DocNode;
			type: 'retry on adf stream errors';
	  }
	| {
			sentiment: PluginFeedbackMetadata['sentiment'];
			type: 'feedback submitted';
	  }
	| {
			type: 'toggle use page knowledge';
	  }
	| {
			baseGenerate: EditorPluginAIConfigItem;
			configItem: EditorPluginAIConfigItem;
			type: 'update baseGenerate and configItem';
	  }
	| {
			type: 'reset';
	  }
	| {
			context: AIExperienceMachineContext;
			type: 'initialise context';
	  }
	| {
			type: 'startStreamingForCreate';
	  },
	Record<string, AnyActorRef>,
	ProvidedActor,
	ParameterizedObject,
	ParameterizedObject,
	string,
	StateValue,
	string,
	unknown,
	NonReducibleUnknown,
	EventObject,
	MetaObject,
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	any
> {
	const machine = createMachine(
		{
			/** @xstate-layout N4IgpgJg5mDOIC5QEECSACAogDwA5gCcBLMAOwGMx0BZAQ3IAsjSwBiAV1wloBcrcA9rCI8iA0rADaABgC6iUIOGjxCkNkQBGAMwA2ABwA6abukB2TZv37tAVgBMmgCwAaEAE8t2w-oCcm+207bXtA-199AF9ItzQsPEISCio6RmYwQ1geWgI+CHQs3jYAayJyYvQBADMqmXkkECURMVI1DQRNM0cfWxsQ7U1bXVsBt08EEOljMzNbX10nW1mh+ydo2IwcfGIyShp6JhZM7NzIAuy+VlLyyprJTXrFIWbVBvbLeyn7Je1pc1NpJYxohJsZ9E5fNJwU5Zk5dNp1iA4ltErsUgd0oYALYCbgAG3Q9FEADcqIVLnByLR8HU1E0VK03oh7L4LIZnGYbPpHE4WfYzMCEL1fIZTCM5mYQty4YjkQkdsl9mkjjj8YTyCSyRc2FkBAQwABhcR8bA8ADKFyIWTKUjkdOeDLaIPsunshhCFjMul8TmkoRmgpdIrhei6phZcN8ss28qSe1ShwyqtoBKJRFJ5yKHC4RUJMFIPFtj0aDpaToQLMB7K9cOk2iccICge9hhD3u0kIc+jmaxiSJj2zj6OVSdxKfVmszfEMYFjaMMeIEtAgzCgU7Y5AEWNweLAfFpDXpZaZHV0XXdzgbZ6G4N82kFASh7pm-P0XQCQQRfblg7RSsT2JjqmGrplqRQznOyQLkuK6kGu5JsOwpDFKQAgAO6kOgVS0EQeIHk8yjHqA7SrMMhhepCThvjCLq2A+vy2D4DYRJ8VFeqs0bxL+ioJpiybAZOCEQdxlDQcuq7rqwsC0FiVBUowVDFGA7j4SWhGvMRXh2KKzjXiYHp6IKb6aIYthONokpnto3JhpxKIKvGGIqkBE6geuwmolBi7iXB7nebBUBSTw+oyapR4aeoWjgt4LoQqEQzer4-JGb4IpWAYUrUb6X4bFxnmOSOgFqmmGZCbOIkZP5ElCVVcHZtwfDoIwtCkCweKoBAYWlhF7zzCKUL6Jo5i2FYfqSoKVnkfW5hvt60hDDl-Z5Q5w4AfxrmldqHkrWJAV+TBq71bmRAQHQuBdepjKaRWYQ+AN9jgn6gKckZZhOMYDjSD6VhJSNi0-vlq18S5JVgdO5WA7t1VbbVgXUkQWE4XhdqHt1V2RRWQSMYNb6Qh2SV3g+UImboBiQgTrodnZkEFWtIMgZt4EQztsP7T5gUELmeJEFiIiQBdLzoyRTiaLohguvouiWBZwzsSlRhDPMnwmA2MK6NTFX-sDxUM2DGTM0OUO+TVB11TwAhKZh3O8zwiO4QLjonqEVYi-yb2SssvgPqRrZnpYPqct8ksa4DWvOTrglbQb86syb7OsMwuDsLb5sCAUDB6rb2H2yjBGC+Wnz+KKrrmZKA1wg+vJi+Tvgjd8IyDCHK1h6OEduWVNOVab8Ew93rBMLgtC0E1xpkFnSMO0RGNhGLb4mMTwwNvYD6aJChj+H4X0spTUbfgOoe8eH46g+50ded3bMBawyAAKoAAroMSYh4rwLToBAe5gBq-O52p+cnqvKid1vSdF5CGRYK9zyDG7INWw5hPhmCbkOFuRVj661Pp3I2PdwKw1YPqWAggJBUFTgUHmuEciTx6lFZw4teS-Asg4RYy8PBeBbPNKWiwQGb3sEgv8h9W5oMjkzTBsctrwztrufICFWDiP1MFFSv9wpC2ZJ9VsgcFidFASYImQRRSzF+tyEYoR-r72bvw1BAl25RxERfISnNGrWxEBtNg2AhK0CqHwAgAAKAASpgAAKj4gAmgAfWQAAGXCQAeQAOqYAACIAEpr6mOQeY9aJ8O4VSwe5exVBHG21BpQ5RFZXRTFdLCBYI0FjmCMqYdeZ4vrsVZBEXeuV7JpKcgIyxjNwY2PZrkrmZDbbMFBjI3ACM5EEAUcWJRBcvqMXmhZMwfoDDemYeMN6wYAgskls4Pwg1eE8S6RY5xGDsn6ifmANCklmCeIIAIKARRinlg5EYQEtd4Si1sL0UYLCOh1m8A4EIqVFhfX8Ec2m2tBFWOERcsAVybnSKQihdCmFs7I1mWjAucJ3oL1+SNPwugHwjCMIEfQX0IS9Elo4SFQMj49L1ttQ2lySBIu1Hgvc0z0C4AeduHgLynZUTdJ8VeKsqILIfG9MW3xpB0PMsMQY6s97LU6YVDJ6CsmQ1ZdcySkAnG8q3LgAVijsUnhGiZbsKyYSpTMr8L2-yEo+ApWeB6NEAi2DpSgjVQi+nwsRe5XlCK2VBRyLbfUVR0iyQLIK66Sq3T2tAaTOYHqHykzdCs1pP0-htKWh0vhJyfWwr9dq4Nuq7FgEjSwaNPBWCUmpGAWNGMlUimlJyeEb16yaAfMZd0DZrBK1CL6T1Kr83HPVfTX1+tME6vZeBCNUax6sCpMkAkC7q1jybe8H5jEQGsmcF9VZUrfjkW9MKeEvpRaINHZ3b1k7i3Tv9Wy3JlbF0FiOo1dgsBCDoETsnLdUUIhMTfBSn5MwDBprmOvP6z0paryiDezW6T729MfaWgNFaq1gBrR+qgX6f1-sKRAWoprLrzJFsXT5kohidDelKgwpkIwui9P7BD7Tb3Ibbqh5l85NxYixC1fIg9dw8EatI64xRqhVHQGhEQDAR78cEzylMe5GqEYAx0LGaiOwHLMKyCD-zErsh+R2GYNgQyaC9ZxmF3Gz6iT4wJ0gQmVOib1rhkeBZZwmqxWRk88IRX+09IS0w95-ktNFPNMBFL5gQjY3mjjhaUNMrsxkBzSnhOqbcyuygmL7S+bjQNRjnJ-C9F5KsImNDuykoiKNIIuaAZmMS1x5LmC0tOeUyJsTHLstgDwg8PL-8406EYj8nQo0-R3gcIGJpopHAfBFnoUIVmms2aZSuWAVICAQEMKM3WdbSC0AAEa7nflaTbnVSODYxiEb4PhHyuwbAlabrJZuWACAt10PDEMHxW4y9y63zuGDGeto7J2Ac5Auz5q77QBjzXZP6QY8xnBJQfHebwUIzyLA7A2AI172NId+2coS4OttA72-Wmkl3HbXQGLoq1OgIgGF9PoabLoLy7LMo4Osu8+yoQ-vABoDW1WJgG9T67Z4ejWr07XcyX0e0mXmHoH5qzaezGW4VQopwpHalF1PEiuzjABFMGUmwQxXD-IGCKWYD1TC+kcJKOLQuC0Tua+uXXVCEAi2xksOV0u7Vy4t9ZcWXbWIPSsDLdXdNXdapWu7kpIZJe+9tbLh14xeSMUlCLIaehggQu+41l3q3zmQ1EUUOPry9OMViklb4pNUrJUdToYPvpbUWUi5H6Ff2Y+G1L9OWG5enbDfZJjmw3yl6CkZ+RPTdZBq+ldNyDvDKifWOyb3jI4iMVnAQgP669cRVUR9FLQOoXxhDW5ORawXYatmRGov7py+4Ul9sVtPJ6ACnOJ39PC9d3vSShr2VifFkUUO8MUEuCwbsO-U5TJFfJ-AZOxIZG2X9A7XWT-EicMVsL6fZZwP6DZRAL0KYCEOKBYW3bsSzfPYXTvB-EtHaWdN3VGfLZtesDPX4VeMpYYaiElc8RYMbKwIOKuSAotWzGdMtOdacINRFVArwO8KaD5Ng3oOjf5ToepWYXSbsGpV1AQpLYvGgkQl9LDGtSQjoaLd0BYXGNQ2jc3U-L0MwcWf0O8CwZNPHeLAnQvLvGAnaTfCAQw1YaQyUayCEQYPTeEOiRQ8ybwbPD1fweBEdfHH7VwqgtDHaNrZzTrPWQwroGw34DKUmefIYAUQzUmZ1PoPwBwM8PTTQ6PLaEnLw+g6HZkWuIwH0PZXSHwoIHtRYUySWZYRYTsSUCoovYnM7CHHbZAzUQwqURZXkO8XHLeUWabVecWdZEYJKOEF0fotw8CaosnMY2osXGHP4auKiODVYiENo-5UVEUIMZHLIuEXoaIaIIAA */
			id: 'AI Experience Machine',

			initial: 'started state',

			types: {
				context: {} as AIExperienceMachineContext,
				events: {} as AIExperienceMachineEvent,
			},

			states: {
				'started state': {
					on: {
						'kick off': [
							{
								target: 'modal active state.experience.error state.active',
								guard: 'no textual content but prompt requires it',
								actions: ['updateFailureContextForNoTextualContent'],
							},
							{
								target: 'modal active state.experience.loading state',
								guard: 'neither base generate nor custom prompt',
							},
							'#AI Experience Machine.modal active state.experience.command palette state',
						],
						'initialise context': {
							actions: ['initialiseContext'],
						},
					},
				},

				'modal active state': {
					type: 'parallel',
					initial: 'experience',

					on: {
						escape: {
							target: '#AI Experience Machine.modal active state.discard.active',
						},
						storeContextStatistics: {
							actions: 'storeContextStatistics',
						},
					},

					states: {
						experience: {
							initial: 'command palette state',
							states: {
								'loading state': {
									on: {
										complete: {
											target: 'review state',
											actions: [
												'pushToHistory',
												'updateMarkdown',
												'updateADFResponse',
												'clearUserInput',
												'updateRovoActions',
											],
										},
										error: {
											target: '#error state.active',
										},

										'same cache key': {
											target: 'review state',
											actions: ['restoreResponseFromHistory', 'clearUserInput'],
										},
									},

									states: {
										loading: {
											invoke: {
												src: fromCallback<AIExperienceMachineEvent, AIExperienceMachineContext>(
													({ input: context, sendBack }) => {
														if (
															context.configItem.key !== CONFIG_ITEM_KEYS.FREE_GENERATE_AIFC &&
															context.streamingHandler
														) {
															context.streamingHandler(context, sendBack);
														}
													},
												),
												input: ({ context }) => context,
											},
											on: {
												stream: {
													target: 'loading',
													actions: ['updateMarkdown', 'updateADFResponse', 'updateLoadingStatus'],
												},

												'update idMap': {
													target: 'loading',
													actions: 'updateIdMap',
												},

												'update traceIds': {
													target: 'loading',
													actions: 'updateTraceIds',
												},

												error: {
													target: '#error state.active',
												},

												'response too similar': {
													target:
														'#AI Experience Machine.modal active state.experience.review state',

													actions: ['pushToHistory', 'clearUserInput', 'updateADFResponse'],
												},

												'update selectionMetaData': {
													target: 'loading',
													actions: 'updateSelectionMetaData',
												},

												'update adfResponseNodeWrappers': {
													target: 'loading',
													actions: 'updateAdfResponseNodeWrappers',
												},
												'retry on adf stream errors': {
													target: 'loading',
													actions: 'retryOnAdfStreamErrors',
												},
											},
										},
									},

									initial: 'loading',
									entry: ['setLatestPrompt', 'updateChannelId'],
									exit: 'clearLoadingStatus',
								},

								'review state': {
									on: {
										interrogate: {
											target: 'loading state',
											actions: [
												'clearFromHistory',
												'updatePromptContextBasedOnTrigger',
												'interrogate',
											],
										},
										refine: {
											target: 'loading state',
											actions: [
												'clearFromHistory',
												'updatePromptContextBasedOnTrigger',
												'incrementRefinementCount',
												'refine',
											],
										},
										error: {
											target: '#error state.active',
										},
										'retry prompt': {
											target: 'loading state',
											actions: [
												'updatePromptContextBasedOnTrigger',
												'updateUserInput',
												'updateUserInputADF',
												'clearMarkdown',
												'incrementRetryPromptCount',
												'clearFromHistory',
											],
										},
										'edit prompt': {
											target: 'command palette state',
											actions: ['editPrompt', 'clearFromHistory'],
										},
										'feedback submitted': {
											target: 'review state',
											actions: ['feedbackSubmitted'],
										},
									},

									states: {
										preview: {
											on: {
												'start refinement': 'refinement',
											},
										},

										refinement: {
											on: {
												'cancel refinement': {
													target: '#AI Experience Machine.modal active state.discard.active',
													actions: ['clearUserInput', 'clearFocus'],
												},
												'update user input': {
													actions: 'updateUserInput',
												},
												'update user input adf': {
													actions: 'updateUserInputADF',
												},
											},
										},
									},

									initial: 'preview',
								},

								'command palette state': {
									on: {
										'kickoff with command palette input': [
											{
												target: 'error state.active',
												guard: 'no textual content but prompt requires it',
												actions: ['updateFailureContextForNoTextualContent'],
											},
											{
												target: 'loading state',
												actions: [
													'updatePromptContextBasedOnTrigger',
													'updateContext',
													'resetRefinementCount',
												],
												// Ignored via go/ees005
												// eslint-disable-next-line @typescript-eslint/no-explicit-any
											} as any,
										],

										'update context': {
											target: 'command palette state',
											actions: ['updateContext'],
											description: `This must be called before 'cancel' transition to make sure the context is updated before the 'if command input is empty' guard is evaluated.`,
										},

										'toggle use page knowledge': {
											target: 'command palette state',
											actions: 'toggleUsePageKnowledge',
										},

										'update baseGenerate and configItem': {
											target: 'command palette state',
											actions: ['updateBaseGenerateAndConfigItem'],
											description: `This must be called when agents are loaded and default baseGenerate and configItem needs to be reset`,
										},
									},

									description: `This state creates a nested state machine that handles all the state of the command palette.

  It needs to know the 'userInput', 'baseGenerate' and 'configItem' from the AI Experience State Machine.

  Whenever we exit this state we need to update the AI Experience State Machine's context with the 'updateContext' action.`,
								},

								//To avoid having to handle errors in every state, all errors are handled here.
								// All error events are 'error' type with the errorInfo object containing further details.
								// The experience application will handle the error and display the appropriate error message
								//based on what has been stored in context on entry to the error state.
								'error state': {
									id: 'error state',
									initial: 'inactive',
									states: {
										inactive: {},
										active: {
											entry: ['updateContext', 'storeFailureContext', 'clearLoadingStatus'],
											exit: ['clearFailureContext'],
											on: {
												retry: {
													target:
														'#AI Experience Machine.modal active state.experience.loading state.loading',
												},
											},
										},
									},
								},
							},
						},

						discard: {
							initial: 'inactive',

							states: {
								inactive: {
									on: {
										'enable discard': {
											target: 'active',
											actions: 'updateContext',
										},
									},
								},
								active: {
									on: {
										'disable discard': {
											target: 'inactive',
											actions: 'updateContext',
										},
										escape: 'inactive',
									},
								},
							},
						},
					},
				},
			},

			context: options.context,

			on: {
				'update positions': {
					actions: 'updatePositions',
				},
				reset: {
					target: '.started state',
					actions: 'resetContext',
				},
			},
		},
		{
			actions: {
				updatePositions: assign(({ event }) => {
					if (event.type !== 'update positions') {
						return {};
					}
					const isEmpty = event.positions[0] === event.positions[1];
					const selectionType: SelectionType = isEmpty ? 'empty' : 'range';
					return { positions: event.positions, isEmpty, selectionType };
				}),

				resetContext: assign(({ event }) => {
					if (event.type !== 'reset') {
						return {};
					}
					// Reset to initial context
					return initialContext;
				}),

				initialiseContext: assign(({ event }) => {
					if (event.type !== 'initialise context') {
						return {};
					}
					// Initialise
					return event.context;
				}),

				storeFailureContext: assign(({ event }) => {
					if (['error'].includes(event.type) && 'errorInfo' in event) {
						return { apiErrorInfo: event.errorInfo };
					}
					return {};
				}),

				updateFailureContextForNoTextualContent: assign(() => {
					return { apiErrorInfo: { failureReason: FAILURE_REASON.NO_TEXTUAL_CONTENT } };
				}),

				clearFailureContext: assign(() => {
					return { apiErrorInfo: {} };
				}),

				updateMarkdown: assign(({ event }) => {
					if (event.type !== 'stream' && event.type !== 'complete') {
						return {};
					}

					return {
						markdown: event.markdown,
						feedbackSubmitted: undefined,
					};
				}),

				updateADFResponse: assign(({ event }) => {
					if (
						event.type !== 'stream' &&
						event.type !== 'complete' &&
						event.type !== 'response too similar'
					) {
						return {};
					}
					return {
						adfResponse: event.adfResponse,
					};
				}),

				updateLoadingStatus: assign(({ event }) => {
					if (event.type !== 'stream') {
						return {};
					}
					// If the loading status is empty, we want to clear it from the context
					if (event.loadingStatus === '') {
						return { loadingStatus: undefined };
					}
					return {
						loadingStatus: event.loadingStatus,
					};
				}),

				clearLoadingStatus: assign(() => {
					return { loadingStatus: undefined };
				}),

				storeContextStatistics: assign(({ event }) => {
					if (event.type === 'storeContextStatistics') {
						return {
							contextStatistics: event.contextStatistics,
						};
					}
					return {};
				}),
				clearMarkdown: assign(({ event }) => {
					if (event.type !== 'retry prompt') {
						return {};
					}
					return {
						markdown: undefined,
						idMap: undefined,
						selectionMetaData: undefined,
						adfResponseNodeWrappers: undefined,
					};
				}),

				interrogate: assign(({ context, event }) => {
					if (event.type !== 'interrogate') {
						return {};
					}
					/**
					 * Need to wipe out markdown as per user story designs where we don't use
					 * the prior response in the latest response
					 */
					return {
						markdown: '',
						adfResponse: undefined,
						configItem:
							context.configItem.key !== CONFIG_ITEM_KEYS.ROVO_AGENT
								? context.baseGenerate
								: context.configItem,
						userInput: event.userInput,
						userADFInput: event.userADFInput,
						latestPromptResponse: event.latestPromptResponse,
					};
				}),

				refine: assign(({ event }) => {
					if (event.type !== 'refine') {
						return {};
					}

					return {
						markdown: '',
						configItem: event.configItem,
						latestPromptResponse: event.latestPromptResponse,
					};
				}),

				clearUserInput: assign(() => {
					return {
						userInput: '',
						userADFInput: undefined,
					};
				}),

				updateUserInput: assign(({ event }) => {
					if (event.type !== 'update user input' && event.type !== 'retry prompt') {
						return {};
					}
					return { userInput: event.input };
				}),

				updateUserInputADF: assign(({ event }) => {
					if (event.type !== 'update user input adf' && event.type !== 'retry prompt') {
						return {};
					}
					return { userADFInput: event.adf };
				}),

				updateRovoActions: assign(({ context, event }) => {
					if (event.type !== 'complete') {
						return {};
					}

					// We don't want to update if there are no rovo actions or
					// an agent hasn't been set
					if (!event.rovoActions?.length || !context.configItem.agent) {
						return { rovoActions: {} };
					}

					return {
						rovoActions: event.rovoActions.reduce<RovoActions>((prevValue, currentValue) => {
							const key = currentValue.data.suggestion === 'insert' ? 'empty' : 'range';
							prevValue[key] = currentValue.data;
							return prevValue;
						}, {}),
					};
				}),

				feedbackSubmitted: assign(({ event }) => {
					if (event.type !== 'feedback submitted') {
						return {};
					}

					return {
						feedbackSubmitted: event.sentiment,
					};
				}),

				pushToHistory: assign(({ context, event }) => {
					if (event.type !== 'complete' && event.type !== 'response too similar') {
						return {};
					}

					const { cacheKey } = event;

					// Check if the most recent entry is the same as the new event to prevent duplicates
					const entries = context.responseHistory.entries;
					if (entries.length > 0 && entries[entries.length - 1].cacheKey === cacheKey) {
						return {};
					}

					let historyEvent;
					if (event.type === 'complete') {
						historyEvent = {
							userInput: context.userInput || '',
							userADFInput: context.userADFInput,
							configItem: context.configItem,
							markdown: event.markdown,
							cacheKey,
							modelInput: event.modelInput,
							inputOutputDiffRatio: event.inputOutputDiffRatio,
							// Ignored via go/ees005
							// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
							latestPrompt: context.latestPrompt!,
						};
					}

					if (event.type === 'response too similar') {
						historyEvent = {
							userInput: context.userInput || '',
							userADFInput: context.userADFInput,
							configItem: context.configItem,
							markdown: '',
							responseTooSimilar: true,
							cacheKey,
							modelInput: event.modelInput,
							inputOutputDiffRatio: event.inputOutputDiffRatio,
							// Ignored via go/ees005
							// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
							latestPrompt: context.latestPrompt!,
						};
					}

					if (!historyEvent) {
						return {};
					}

					// Calculate the size of the historyEvent object for analytics
					const historyEventSize = new Blob([JSON.stringify(historyEvent)]).size;
					const totalSize = context.responseHistory.totalSize + historyEventSize;

					// Add the new event to the end of the entries array
					const updatedEntries = [...entries, historyEvent];
					// Ensure the entries array does not exceed 5 items to limit memory usage
					if (updatedEntries.length > 5) {
						updatedEntries.shift();
					}

					return {
						responseHistory: {
							entries: updatedEntries,
							positionFromEnd: 0, // Reset the position
							totalSize,
							traversals: context.responseHistory.traversals,
						},
					};
				}),

				restoreResponseFromHistory: assign(({ context, event }) => {
					if (event.type !== 'same cache key') {
						return {};
					}

					const { entries } = context.responseHistory;
					const historyEvent = entries[entries.length - 1];

					if (!historyEvent) {
						return {};
					}

					return {
						userInput: historyEvent.userInput,
						userADFInput: historyEvent.userADFInput,
						markdown: historyEvent.markdown,
						configItem: historyEvent.configItem,
						latestPromptResponse: historyEvent.markdown,
					};
				}),
				clearFromHistory: assign(({ event }) => {
					if (event.type !== 'retry prompt') {
						return {};
					}

					return {
						responseHistory: { entries: [], positionFromEnd: 0, totalSize: 0, traversals: 0 },
					};
				}),

				setLatestPrompt: enqueueActions(({ enqueue, check }) => {
					if (check({ type: 'is base generate' })) {
						enqueue(
							assign(({ context }) => ({
								latestPrompt: {
									userInput: context?.userInput || '',
									userADFInput: context?.userADFInput,
								},
							})),
						);
					} else if (check({ type: 'has custom prompt' })) {
						enqueue(
							assign(({ context, event }) => ({
								latestPrompt: {
									...(event.type !== 'interrogate' &&
										event.type !== 'refine' && {
											presetTitle: context.configItem,
										}),
									userInput: context?.userInput || '',
									userADFInput: context?.userADFInput,
								},
							})),
						);
					} else {
						/**
						 * Falls through to options which do not take additional
						 * user input and is not base generate, e.g.
						 * - Improve writing
						 * - Summarize
						 * Upon interrogation, there may also be user input.
						 * We preference showing user input over the preset title for
						 * non-partial presets.
						 */
						enqueue(
							assign(({ context }) =>
								context?.userInput
									? {
											latestPrompt: {
												userInput: context?.userInput || '',
												userADFInput: context?.userADFInput,
											},
										}
									: {
											latestPrompt: {
												presetTitle: context.configItem,
											},
										},
							),
						);
					}
				}),
				clearFocus: () => {
					(document.activeElement as HTMLElement | null)?.blur();
				},

				updateContext: assign(({ event }) => {
					if (
						event.type === 'kickoff with command palette input' ||
						event.type === 'update context' ||
						event.type === 'enable discard'
					) {
						return { ...event.context };
					}
					if (event.type === 'disable discard') {
						return { onceDiscard: undefined };
					}
					return {};
				}),
				updateBaseGenerateAndConfigItem: assign(({ event, context }) => {
					if (event.type === 'update baseGenerate and configItem') {
						/**
						 * If user has selected any prompt, then don't change configItem.
						 */
						if (context.configItem.key === CONFIG_ITEM_KEYS.FREE_GENERATE) {
							return {
								baseGenerate: event.baseGenerate,
								configItem: event.configItem,
							};
						} else {
							return {
								baseGenerate: event.baseGenerate,
							};
						}
					}
					return {};
				}),
				updateChannelId: assign(({ context }) => {
					const { aiProvider, configItem } = context;

					if (!aiProvider.getChannelVariables || !configItem.agent) {
						return {
							channelId: undefined,
							previousAgentId: undefined,
						};
					}

					// if agent did not change, we don't need to update channelId preserving the same conversation history
					// as soon as agent changes, we need to recreate channelId to start a new conversation
					if (configItem.agent.id === context.previousAgentId) {
						return {};
					}

					return {
						channelId: buildChannelId(),
						previousAgentId: configItem.agent.id,
					};
				}),
				updateIdMap: assign(({ context, event }) => {
					if (event.type === 'update idMap') {
						return { idMap: { ...context.idMap, ...event.context.idMap } };
					}
					return {};
				}),
				updateTraceIds: assign(({ event }) => {
					if (event.type === 'update traceIds') {
						return { traceIds: event.context.traceIds };
					}
					return {};
				}),
				updateSelectionMetaData: assign(({ event }) => {
					if (event.type === 'update selectionMetaData') {
						return { selectionMetaData: event.context.selectionMetaData };
					}
					return {};
				}),
				updateAdfResponseNodeWrappers: assign(({ event }) => {
					if (event.type === 'update adfResponseNodeWrappers') {
						return { adfResponseNodeWrappers: event.context.adfResponseNodeWrappers };
					}
					return {};
				}),
				retryOnAdfStreamErrors: assign(({ event }) => {
					if (event.type === 'retry on adf stream errors') {
						return { adfResponse: event.adfResponse, markdown: '' };
					}
					return {};
				}),
				editPrompt: assign(({ context, event }) => {
					if (event.type !== 'edit prompt') {
						return {};
					}

					// from 'neither base generate nor custom prompt'
					const isBaseGenerate = context.configItem.title === context.baseGenerate?.title;
					const hasCustomPrompt = context.configItem.promptLabel !== undefined;
					// if it's a preset prompt which doesn't need user input (like `Summarize writing`)
					// - we want to reset configItem
					// - we want to reset the user input
					const isPresetPrompt = !isBaseGenerate && !hasCustomPrompt;
					let configItem = context.configItem;

					// if the user selects a submenu via a parent (`Change tone` -> `Neutral`)
					// - we don't want to reset the configItem
					//   - (keep the configItem) so that we can restore the selected parent later in UserInputCommandPaletteWithLogic
					// - we want to reset the user input
					const isSubmenuSelected = Boolean(context.parentPresetPromptLabel);

					// when we add default agent for knowledge source, it doesn't need additional user input because it's triggered from the user input
					// - we want to reset the configItem
					// - we don't want to reset the user input
					const isDefaultAgent = Boolean(context.configItem.agent?.isDefault);

					const resetConfigItem = (isPresetPrompt || isDefaultAgent) && !isSubmenuSelected;
					const resetUserInput = (isPresetPrompt || isSubmenuSelected) && !isDefaultAgent;

					if (resetConfigItem && context.baseGenerate) {
						// from dismissCustomPrompt
						configItem = context.baseGenerate;
					}

					// If we detect a preset prompt, clear everything
					// otherwise preserve what we can.
					return {
						markdown: undefined,
						userInput: resetUserInput ? undefined : event.userInput,
						userADFInput: resetUserInput ? undefined : event.userADFInput,
						presetTitle: resetUserInput ? undefined : event.presetTitle,
						configItem,
						latestPromptResponse: undefined,
						editCount: context.editCount + 1,
						idMap: undefined,
						selectionMetaData: undefined,
						adfResponseNodeWrappers: undefined,
					};
				}),
				incrementRetryPromptCount: assign(({ context }) => {
					return {
						retryPromptCount: context.retryPromptCount + 1,
					};
				}),

				incrementRefinementCount: assign(({ context }) => {
					return {
						refinementCount: context.refinementCount + 1,
					};
				}),

				resetRefinementCount: assign(() => {
					return {
						refinementCount: 0,
					};
				}),

				updatePromptContextBasedOnTrigger: assign(({ event }) => {
					if (event.type === 'interrogate') {
						return {
							promptTrigger: 'interrogate' as PromptTrigger,
							latestPromptRetried: false,
						};
					}
					if (event.type === 'refine') {
						return {
							promptTrigger: 'refinementButton' as PromptTrigger,
							latestPromptRetried: false,
						};
					}
					if (event.type === 'retry prompt') {
						return { latestPromptRetried: true };
					}
					if (event.type === 'kickoff with command palette input') {
						return {
							promptTrigger: 'generate' as PromptTrigger,
							latestPromptRetried: false,
						};
					}
					return {};
				}),

				toggleUsePageKnowledge: assign(({ context, event }) => {
					if (event.type !== 'toggle use page knowledge') {
						return {};
					}
					// This is set to true by default
					return {
						usePageKnowledge: !context.usePageKnowledge,
					};
				}),
			},
			guards: {
				['has custom prompt']: ({ context }) => {
					// when we add default agent for knowledge source, it doesn't need additional user input because it's triggered from the user input
					const isDefaultAgent = Boolean(context.configItem.agent?.isDefault);
					if (isDefaultAgent) {
						return false;
					}

					return context.configItem.promptLabel !== undefined;
				},
				['is base generate']: ({ context }) => {
					// Doing configItem direct comparision fails in tests where we mutate or create custom
					// items.
					return context.configItem.title === context.baseGenerate?.title;
				},
				['neither base generate nor custom prompt']: ({ context }) => {
					const isBaseGenerate = context.configItem.title === context.baseGenerate?.title;
					const hasCustomPrompt = context.configItem.promptLabel !== undefined;
					return !isBaseGenerate && !hasCustomPrompt;
				},
				['if command input is empty']: ({ context }) => {
					return context.userInput?.trim?.()?.length === 0;
				},
				['no textual content but prompt requires it']: ({ context, event }) => {
					if (!fg('platform_editor_ai_handle_invalid_input')) {
						return false;
					}
					let configItem = context.configItem;
					if (event.type === 'kickoff with command palette input') {
						configItem = event.context.configItem;
					}

					const requiresTextualContent = configItem.requiresTextualContent;
					const isBaseGenerate = configItem.title === context.baseGenerate?.title;
					const hasCustomPrompt = configItem.promptLabel !== undefined;
					const doesPromptRequireTextualContent =
						requiresTextualContent && !isBaseGenerate && !hasCustomPrompt;
					if (doesPromptRequireTextualContent) {
						const state = context.editorView.state;
						/**
						 * If selection is empty then prompt will trigger on the entire document.
						 * So checking if doc has textual content.
						 */
						return !hasTextualContent(
							state,
							context.isEmpty ? [0, state.doc.content.size] : context.positions,
						);
					}
					return false;
				},
			},
		},
	);

	return machine;
}

// We will try to remove this type in separate PR
export type AIExperienceMachineState =
	| {
			['modal active state']: {
				discard: 'active' | 'inactive';
				experience:
					| { ['loading state']: 'loading' }
					| { ['review state']: 'preview' | 'refinement' }
					| 'command palette state'
					| { ['error state']: 'inactive' | 'active' };
			};
	  }
	| 'started state';

type ModelInput = {
	/**
	 * This is the document in markdown format, that will be sent to the AI
	 */
	doc?: string;
	/**
	 * This is the selection in markdown format, that will be sent to the AI
	 *
	 * note -- this will only be available for range selections
	 */
	selection?: string;
};

// Typed explicitly due to old xstate version not having
// type generation
// Whenever adding a new event to the machine -- you will need to update this union
export type AIExperienceMachineEvent =
	| {
			context: {
				configItem: EditorPluginAIConfigItem;
				parentPresetPromptLabel?: string;
				userADFInput?: JSONDocNode;
				userInput: string;
			};
			type: 'kickoff with command palette input';
	  }
	| {
			adfResponse?: DocNode;
			loadingStatus?: string;
			markdown: string;
			type: 'stream';
	  }
	| {
			adfResponse?: DocNode;
			cacheKey: string;
			inputOutputDiffRatio?: string;
			markdown: string;
			modelInput: ModelInput;
			rovoActions?: ConvoAIResponseRovoAction[];
			type: 'complete';
	  }
	| {
			cacheKey?: string;
			errorInfo: {
				apiName?: string;
				errorContent?: string;
				errorKey?: string;
				failureReason: FAILURE_REASON;
				statusCode?: number;
			};
			modelInput?: ModelInput;
			retryAfter?: number;
			type: 'error';
	  }
	// Error state transition
	| { type: 'retry' }
	| {
			latestPromptResponse: string;
			type: 'interrogate';
			userADFInput?: JSONDocNode;
			userInput: string;
	  }
	| {
			configItem: EditorPluginAIConfigItem;
			latestPromptResponse: string;
			type: 'refine';
	  }
	| { type: 'kick off' }
	| { type: 'same cache key' }
	// Nested user input events
	| { positions: Positions; type: 'update positions' }
	| { input: string; type: 'update user input' }
	| { adf?: JSONDocNode; type: 'update user input adf' }
	| {
			adf: JSONDocNode | undefined;
			input: string | undefined;
			type: 'retry prompt';
	  }
	| {
			presetTitle?: string;
			type: 'edit prompt';
			userADFInput?: JSONDocNode;
			userInput?: string;
	  }
	| {
			context: {
				configItem: EditorPluginAIConfigItem;
				userADFInput?: JSONDocNode;
				userInput: string;
			};
			type: 'update context';
	  }
	| {
			context: {
				idMap: IDMap;
			};
			type: 'update idMap';
	  }
	| {
			context: {
				traceIds: string[];
			};
			type: 'update traceIds';
	  }
	| {
			context: {
				selectionMetaData: {
					sliceOpenEnd?: number;
					sliceOpenStart?: number;
				};
			};
			type: 'update selectionMetaData';
	  }
	| {
			context: {
				adfResponseNodeWrappers: string[];
			};
			type: 'update adfResponseNodeWrappers';
	  }
	| { type: 'start refinement' }
	| { type: 'cancel refinement' }
	// discard flow
	| { context?: { onceDiscard?: () => unknown }; type: 'enable discard' }
	| { type: 'disable discard' }
	| { type: 'escape' }
	| {
			contextStatistics: ContextStatistics;
			type: 'storeContextStatistics';
	  }
	| {
			adfResponse?: DocNode;
			cacheKey: string;
			inputOutputDiffRatio?: string;
			modelInput: ModelInput;
			type: 'response too similar';
	  }
	| {
			adfResponse: DocNode;
			type: 'retry on adf stream errors';
	  }
	| {
			sentiment: PluginFeedbackMetadata['sentiment'];
			type: 'feedback submitted';
	  }
	| {
			type: 'toggle use page knowledge';
	  }
	| {
			baseGenerate: EditorPluginAIConfigItem;
			configItem: EditorPluginAIConfigItem;
			type: 'update baseGenerate and configItem';
	  }
	| { type: 'reset' }
	| {
			context: AIExperienceMachineContext;
			type: 'initialise context';
	  }
	| {
			type: 'startStreamingForCreate';
	  };
